import { apiRequest } from '../../../shared/services/apiClient';
import type {
  AiAutoFunctionInvocationPolicy,
  AiKernelFunctionPolicy,
  AiKernelFunctionSetting,
  AiKernelFunctionToolPlugin,
  AiKernelPluginInstruction,
  AiToolHistoryInclusion,
  AiResourceListPrefetchPreference,
  AiProviderSettings,
  AiPromptConfig,
  AiPromptTemplateVariable,
  AiPromptVersion,
  AiPromptTemplateTypeValue,
  UpdateAiPromptConfigRequest,
  UpdateAiProviderSettingsRequest,
  AiChatProviderOption,
  AiProviderId,
  AiAzureOpenAiConfiguration,
  AiOpenAiConfiguration,
  AiDeepSeekConfiguration,
  AiAnthropicConfiguration,
  AiGoogleGeminiConfiguration,
  AiMistralAiConfiguration,
  AiProviderConfiguration,
  UpdateAiProviderConfigurationRequest,
  AiExternalChannelOverrideStatus,
} from '../types/AiPromptConfig';

const normalizeAiPromptTemplateVariable = (value: any): AiPromptTemplateVariable => ({
  name: String(value?.name ?? ''),
  description: String(value?.description ?? ''),
  example: String(value?.example ?? ''),
});

const normalizeAiPromptConfig = (value: any): AiPromptConfig => ({
  templateType: Number(value?.templateType ?? 0) as AiPromptTemplateTypeValue,
  systemPrompt: value?.systemPrompt ? String(value.systemPrompt) : null,
  toneOfVoice: value?.toneOfVoice ? String(value.toneOfVoice) : null,
  includeCompanyKnowledge: Boolean(value?.includeCompanyKnowledge ?? true),
  includeConversationHistory: Boolean(value?.includeConversationHistory ?? true),
  maxTokensResponse: value?.maxTokensResponse != null ? Number(value.maxTokensResponse) : null,
  customFields: value?.customFields ? String(value.customFields) : null,
  version: Number(value?.version ?? 0),
  isDefault: Boolean(value?.isDefault ?? true),
  isInherited: Boolean(value?.isInherited ?? false),
  inheritsFromUseCaseId: value?.inheritsFromUseCaseId ? String(value.inheritsFromUseCaseId) : null,
  useCaseId: value?.useCaseId ? String(value.useCaseId) : null,
  availableVariables: Array.isArray(value?.availableVariables)
    ? value.availableVariables.map(normalizeAiPromptTemplateVariable)
    : [],
});

const normalizeAiPromptVersion = (value: any): AiPromptVersion => ({
  version: Number(value?.version ?? 0),
  systemPrompt: String(value?.systemPrompt ?? ''),
  toneOfVoice: value?.toneOfVoice ? String(value.toneOfVoice) : null,
  includeCompanyKnowledge: Boolean(value?.includeCompanyKnowledge ?? true),
  includeConversationHistory: Boolean(value?.includeConversationHistory ?? true),
  maxTokensResponse: value?.maxTokensResponse != null ? Number(value.maxTokensResponse) : null,
  customFields: value?.customFields ? String(value.customFields) : null,
  changedAt: String(value?.changedAt ?? ''),
  changeReason: value?.changeReason ? String(value.changeReason) : null,
});

const normalizeKernelFunctionToolPlugin = (value: any): AiKernelFunctionToolPlugin => ({
  pluginId: String(value?.pluginId ?? ''),
  displayName: String(value?.displayName ?? ''),
  kernelPluginName: String(value?.kernelPluginName ?? ''),
  isPublished: value?.isPublished !== false,
});

const normalizeKernelFunctionSetting = (value: any): AiKernelFunctionSetting => ({
  functionId: String(value?.functionId ?? ''),
  functionDisplayName: String(value?.functionDisplayName ?? ''),
  pluginName: String(value?.pluginName ?? ''),
  accessMode: String(value?.accessMode ?? 'read').toLowerCase() === 'write' ? 'write' : 'read',
  enabled: Boolean(value?.enabled ?? false),
  instruction: value?.instruction ? String(value.instruction) : null,
  supportsInstruction: Boolean(value?.supportsInstruction ?? false),
  includeResultsInHistory: Boolean(value?.includeResultsInHistory ?? false),
  supportsHistoryInclusion: Boolean(value?.supportsHistoryInclusion ?? false),
  toolPlugins: Array.isArray(value?.toolPlugins)
    ? value.toolPlugins.map(normalizeKernelFunctionToolPlugin)
    : [],
});

const normalizeKernelPluginInstruction = (value: any): AiKernelPluginInstruction => ({
  functionId: String(value?.functionId ?? ''),
  instruction: value?.instruction ? String(value.instruction) : null,
});

const normalizeToolHistoryInclusion = (value: any): AiToolHistoryInclusion => ({
  functionId: String(value?.functionId ?? ''),
  includeResultsInHistory: Boolean(value?.includeResultsInHistory ?? false),
});

const normalizeKernelFunctionPolicy = (value: any): AiKernelFunctionPolicy => ({
  useCaseId: String(value?.useCaseId ?? ''),
  isConfigured: Boolean(value?.isConfigured ?? false),
  functions: Array.isArray(value?.functions) ? value.functions.map(normalizeKernelFunctionSetting) : [],
  toolPlugins: Array.isArray(value?.toolPlugins)
    ? value.toolPlugins.map(normalizeKernelFunctionToolPlugin)
    : [],
});

const normalizeAutoFunctionInvocationPolicy = (value: any): AiAutoFunctionInvocationPolicy => ({
  useCaseId: String(value?.useCaseId ?? ''),
  isConfigured: Boolean(value?.isConfigured ?? false),
  maxToolCalls: Number(value?.maxToolCalls ?? 1),
});

const normalizeResourceListPrefetchPreference = (value: any): AiResourceListPrefetchPreference => ({
  useCaseId: String(value?.useCaseId ?? ''),
  isConfigured: Boolean(value?.isConfigured ?? false),
  enabled: Boolean(value?.enabled ?? true),
});

const normalizeChatProviderOption = (value: unknown): AiChatProviderOption => {
  const normalized = String(value ?? 'Auto');
  if (
    normalized === 'AzureOpenAI'
    || normalized === 'OpenAI'
    || normalized === 'DeepSeek'
    || normalized === 'Anthropic'
    || normalized === 'GoogleGemini'
    || normalized === 'MistralAI'
    || normalized === 'Auto'
  ) {
    return normalized;
  }

  return 'Auto';
};

const normalizeProviderSetting = (value: any) => {
  const id = String(value?.id ?? '');
  const displayNames: Record<string, string> = {
    AzureOpenAI: 'Azure OpenAI',
    OpenAI: 'OpenAI',
    DeepSeek: 'DeepSeek',
    Anthropic: 'Anthropic',
    GoogleGemini: 'Google Gemini',
    MistralAI: 'Mistral AI',
  };

  return {
  id,
  displayName: displayNames[id] ?? String(value?.displayName ?? ''),
  isConfiguredAtDeployment: Boolean(value?.isConfiguredAtDeployment ?? false),
  isTenantConfigured: Boolean(value?.isTenantConfigured ?? false),
  isConfigured: Boolean(value?.isConfigured ?? value?.isConfiguredAtDeployment ?? false),
  enabled: Boolean(value?.enabled ?? false),
  effectiveEnabled: Boolean(value?.effectiveEnabled ?? false),
  model: value?.model ? String(value.model) : null,
  endpoint: value?.endpoint ? String(value.endpoint) : null,
  };
};

const normalizeProviderSettings = (value: any): AiProviderSettings => ({
  isConfigured: Boolean(value?.isConfigured ?? false),
  chatProvider: normalizeChatProviderOption(value?.chatProvider),
  activeChatProvider: String(value?.activeChatProvider ?? 'None'),
  activeChatModelId: String(value?.activeChatModelId ?? ''),
  providers: Array.isArray(value?.providers) ? value.providers.map(normalizeProviderSetting) : [],
});

export const getAiPromptConfig = async (
  templateType: AiPromptTemplateTypeValue,
): Promise<AiPromptConfig> => {
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/${templateType}`, {
    method: 'GET',
  });
  return normalizeAiPromptConfig(result);
};

export const updateAiPromptConfig = async (
  templateType: AiPromptTemplateTypeValue,
  request: UpdateAiPromptConfigRequest,
): Promise<AiPromptConfig> => {
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/${templateType}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return normalizeAiPromptConfig(result);
};

export const getAiPromptVersionHistory = async (
  templateType: AiPromptTemplateTypeValue,
): Promise<AiPromptVersion[]> => {
  const result = await apiRequest<unknown[]>(`/api/v1/settings/ai/${templateType}/history`, {
    method: 'GET',
  });
  return Array.isArray(result) ? result.map(normalizeAiPromptVersion) : [];
};

export const revertAiPromptToVersion = async (
  templateType: AiPromptTemplateTypeValue,
  versionNumber: number,
): Promise<AiPromptConfig> => {
  const result = await apiRequest<unknown>(
    `/api/v1/settings/ai/${templateType}/revert/${versionNumber}`,
    { method: 'POST' },
  );
  return normalizeAiPromptConfig(result);
};

export const resetAiPromptToDefault = async (
  templateType: AiPromptTemplateTypeValue,
): Promise<void> => {
  await apiRequest<unknown>(`/api/v1/settings/ai/${templateType}/reset`, {
    method: 'POST',
  });
};

export const getAiUseCasePromptConfig = async (
  useCaseId: string,
): Promise<AiPromptConfig> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/prompt`, {
    method: 'GET',
  });
  return normalizeAiPromptConfig(result);
};

export const updateAiUseCasePromptConfig = async (
  useCaseId: string,
  request: UpdateAiPromptConfigRequest,
): Promise<AiPromptConfig> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/prompt`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return normalizeAiPromptConfig(result);
};

export const getAiUseCasePromptVersionHistory = async (
  useCaseId: string,
): Promise<AiPromptVersion[]> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown[]>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/prompt/history`, {
    method: 'GET',
  });
  return Array.isArray(result) ? result.map(normalizeAiPromptVersion) : [];
};

export const resetAiUseCasePromptConfig = async (
  useCaseId: string,
): Promise<AiPromptConfig> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/prompt/reset`, {
    method: 'POST',
  });
  return normalizeAiPromptConfig(result);
};

export const getAiExternalChannelOverrides = async (
  parentUseCaseId: string,
): Promise<AiExternalChannelOverrideStatus[]> => {
  const encodedUseCase = encodeURIComponent(parentUseCaseId);
  const result = await apiRequest<unknown[]>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/channel-overrides`, {
    method: 'GET',
  });
  if (!Array.isArray(result)) {
    return [];
  }

  return result.map((item) => ({
    channelKey: String((item as { channelKey?: string })?.channelKey ?? ''),
    useCaseId: String((item as { useCaseId?: string })?.useCaseId ?? ''),
    isCustomized: Boolean((item as { isCustomized?: boolean })?.isCustomized),
  }));
};

export const clearAiExternalChannelOverride = async (
  useCaseId: string,
): Promise<void> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/channel-override`, {
    method: 'DELETE',
  });
};

export const getAiKernelFunctionPolicy = async (
  useCaseId: string,
): Promise<AiKernelFunctionPolicy> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/tool-functions`, {
    method: 'GET',
  });

  return normalizeKernelFunctionPolicy(result);
};

export const updateAiKernelFunctionPolicy = async (
  useCaseId: string,
  allowedFunctionIds: string[],
): Promise<AiKernelFunctionPolicy> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/tool-functions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ allowedFunctionIds }),
  });

  return normalizeKernelFunctionPolicy(result);
};

export const updateAiKernelPluginInstruction = async (
  functionId: string,
  instruction: string | null,
): Promise<AiKernelPluginInstruction> => {
  const encodedFunctionId = encodeURIComponent(functionId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/tool-functions/${encodedFunctionId}/instruction`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ instruction }),
  });

  return normalizeKernelPluginInstruction(result);
};

export const updateAiToolHistoryInclusion = async (
  functionId: string,
  includeResultsInHistory: boolean,
): Promise<AiToolHistoryInclusion> => {
  const encodedFunctionId = encodeURIComponent(functionId);
  const result = await apiRequest<unknown>(
    `/api/v1/settings/ai/tool-functions/${encodedFunctionId}/conversation-history`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ includeResultsInHistory }),
    },
  );

  return normalizeToolHistoryInclusion(result);
};

export const getAiAutoFunctionInvocationPolicy = async (
  useCaseId: string,
): Promise<AiAutoFunctionInvocationPolicy> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/auto-function-invocation`, {
    method: 'GET',
  });

  return normalizeAutoFunctionInvocationPolicy(result);
};

export const updateAiAutoFunctionInvocationPolicy = async (
  useCaseId: string,
  maxToolCalls: number,
): Promise<AiAutoFunctionInvocationPolicy> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/auto-function-invocation`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ maxToolCalls }),
  });

  return normalizeAutoFunctionInvocationPolicy(result);
};

export const getAiResourceListPrefetchPreference = async (
  useCaseId: string,
): Promise<AiResourceListPrefetchPreference> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/resource-list-prefetch`, {
    method: 'GET',
  });

  return normalizeResourceListPrefetchPreference(result);
};

export const updateAiResourceListPrefetchPreference = async (
  useCaseId: string,
  enabled: boolean,
): Promise<AiResourceListPrefetchPreference> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/resource-list-prefetch`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });

  return normalizeResourceListPrefetchPreference(result);
};

export const clearAiResourceListPrefetchPreference = async (
  useCaseId: string,
): Promise<AiResourceListPrefetchPreference> => {
  const encodedUseCase = encodeURIComponent(useCaseId);
  const result = await apiRequest<unknown>(`/api/v1/settings/ai/use-cases/${encodedUseCase}/resource-list-prefetch`, {
    method: 'DELETE',
  });

  return normalizeResourceListPrefetchPreference(result);
};

export const getAiProviderSettings = async (): Promise<AiProviderSettings> => {
  const result = await apiRequest<unknown>('/api/v1/settings/ai/providers', {
    method: 'GET',
  });

  return normalizeProviderSettings(result);
};

export const updateAiProviderSettings = async (
  request: UpdateAiProviderSettingsRequest,
): Promise<AiProviderSettings> => {
  const result = await apiRequest<unknown>('/api/v1/settings/ai/providers', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  return normalizeProviderSettings(result);
};

export const clearAiProviderSettings = async (): Promise<AiProviderSettings> => {
  const result = await apiRequest<unknown>('/api/v1/settings/ai/providers', {
    method: 'DELETE',
  });

  return normalizeProviderSettings(result);
};

const normalizeAzureOpenAiConfiguration = (value: any): AiAzureOpenAiConfiguration => ({
  isTenantConfigured: Boolean(value?.isTenantConfigured ?? false),
  hasApiKey: Boolean(value?.hasApiKey ?? false),
  endpoint: String(value?.endpoint ?? ''),
  chatDeploymentName: String(value?.chatDeploymentName ?? ''),
  embeddingDeploymentName: String(value?.embeddingDeploymentName ?? ''),
  audioTranscriptionDeploymentName: String(value?.audioTranscriptionDeploymentName ?? ''),
  apiVersion: String(value?.apiVersion ?? ''),
});

const normalizeOpenAiConfiguration = (value: any): AiOpenAiConfiguration => ({
  isTenantConfigured: Boolean(value?.isTenantConfigured ?? false),
  hasApiKey: Boolean(value?.hasApiKey ?? false),
  model: String(value?.model ?? ''),
  embeddingModel: String(value?.embeddingModel ?? ''),
  temperature: Number(value?.temperature ?? 0.2),
  maxOutputTokens: Number(value?.maxOutputTokens ?? 1024),
});

const normalizeDeepSeekConfiguration = (value: any): AiDeepSeekConfiguration => ({
  isTenantConfigured: Boolean(value?.isTenantConfigured ?? false),
  hasApiKey: Boolean(value?.hasApiKey ?? false),
  endpoint: String(value?.endpoint ?? ''),
  model: String(value?.model ?? ''),
  thinkingEnabled: Boolean(value?.thinkingEnabled ?? false),
});

const normalizeAnthropicConfiguration = (value: any): AiAnthropicConfiguration => ({
  isTenantConfigured: Boolean(value?.isTenantConfigured ?? false),
  hasApiKey: Boolean(value?.hasApiKey ?? false),
  model: String(value?.model ?? ''),
  maxOutputTokens: Number(value?.maxOutputTokens ?? 1024),
});

const normalizeGoogleGeminiConfiguration = (value: any): AiGoogleGeminiConfiguration => ({
  isTenantConfigured: Boolean(value?.isTenantConfigured ?? false),
  hasApiKey: Boolean(value?.hasApiKey ?? false),
  model: String(value?.model ?? ''),
});

const normalizeMistralAiConfiguration = (value: any): AiMistralAiConfiguration => ({
  isTenantConfigured: Boolean(value?.isTenantConfigured ?? false),
  hasApiKey: Boolean(value?.hasApiKey ?? false),
  model: String(value?.model ?? ''),
});

export const getAiProviderConfiguration = async (
  providerId: AiProviderId,
): Promise<AiProviderConfiguration> => {
  const result = await apiRequest<unknown>(
    `/api/v1/settings/ai/providers/${encodeURIComponent(providerId)}/configuration`,
    { method: 'GET' },
  );

  switch (providerId) {
    case 'AzureOpenAI':
      return normalizeAzureOpenAiConfiguration(result);
    case 'OpenAI':
      return normalizeOpenAiConfiguration(result);
    case 'DeepSeek':
      return normalizeDeepSeekConfiguration(result);
    case 'Anthropic':
      return normalizeAnthropicConfiguration(result);
    case 'GoogleGemini':
      return normalizeGoogleGeminiConfiguration(result);
    case 'MistralAI':
      return normalizeMistralAiConfiguration(result);
    default:
      return normalizeOpenAiConfiguration(result);
  }
};

export const updateAiProviderConfiguration = async (
  providerId: AiProviderId,
  request: UpdateAiProviderConfigurationRequest,
): Promise<AiProviderConfiguration> => {
  const result = await apiRequest<unknown>(
    `/api/v1/settings/ai/providers/${encodeURIComponent(providerId)}/configuration`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  );

  switch (providerId) {
    case 'AzureOpenAI':
      return normalizeAzureOpenAiConfiguration(result);
    case 'OpenAI':
      return normalizeOpenAiConfiguration(result);
    case 'DeepSeek':
      return normalizeDeepSeekConfiguration(result);
    case 'Anthropic':
      return normalizeAnthropicConfiguration(result);
    case 'GoogleGemini':
      return normalizeGoogleGeminiConfiguration(result);
    case 'MistralAI':
      return normalizeMistralAiConfiguration(result);
    default:
      return normalizeOpenAiConfiguration(result);
  }
};

export const clearAiProviderConfiguration = async (
  providerId: AiProviderId,
): Promise<AiProviderConfiguration> => {
  const result = await apiRequest<unknown>(
    `/api/v1/settings/ai/providers/${encodeURIComponent(providerId)}/configuration`,
    { method: 'DELETE' },
  );

  switch (providerId) {
    case 'AzureOpenAI':
      return normalizeAzureOpenAiConfiguration(result);
    case 'OpenAI':
      return normalizeOpenAiConfiguration(result);
    case 'DeepSeek':
      return normalizeDeepSeekConfiguration(result);
    case 'Anthropic':
      return normalizeAnthropicConfiguration(result);
    case 'GoogleGemini':
      return normalizeGoogleGeminiConfiguration(result);
    case 'MistralAI':
      return normalizeMistralAiConfiguration(result);
    default:
      return normalizeOpenAiConfiguration(result);
  }
};
